import { formatPercent, STRENGTH_LABEL } from "./colors";
import { STATES } from "./geo";
import {
  type County2024,
  crosstabs,
  GOVERNOR_INCUMBENCY,
  GOVERNOR_PULL,
  INDEPENDENT_KEEP,
  nationalGeneric,
  NON_VOTER_SHARE,
  recallCrosstabs,
  strengthFor,
  trumpTwoParty,
} from "./model";
import { RACES } from "./races";
import { shortName } from "./survey";
import type {
  Candidate,
  Component,
  ForesightEstimate,
  ModelSettings,
  Office,
  Party,
  Race,
  Side,
  StateSample,
} from "./types";
import {
  buildDistrictRows,
  buildRows,
  describeSide,
  type DistrictRow,
  districtSides,
  GOVERNOR_HOLDOVERS,
  governorCounts,
  houseProjection,
  OFFICE_LABEL,
  type RaceRow,
  SENATE_HOLDOVERS,
  senateProjection,
} from "./view";

/* ── Constants ─────────────────────────────────── */

/** Races (or House districts) listed as the closest. */
const CLOSEST = 8;

/** Flipped House seats given as examples. */
const FLIP_EXAMPLES = 6;

/** The question each office's national key insights answer. */
export const NATIONAL_QUESTIONS: Record<Office, string> = {
  governor: "Which party is favored to win the 2026 governor races?",
  house:
    "Which party is favored to control the U.S. House after the November 3, 2026 midterms?",
  senate:
    "Which party is favored to control the U.S. Senate after the November 3, 2026 midterms?",
};

const PARTY_NAME: Record<Party, string> = {
  D: "Democrats",
  I: "independents",
  R: "Republicans",
};

/* ── Figures ───────────────────────────────────── */

/**
 * An office's national figures as plain text, for its AI summary to be
 * written from. Always Foresight's own read, whatever the map's layer and
 * compared source. Null before any state is read for the office.
 */
export const nationalFigures = ({
  county2024,
  edge,
  environment,
  office,
  samples,
  settings,
}: {
  county2024: County2024 | null;
  /** `turnoutEdge` of these samples. */
  edge: number;
  /** `nationalEnvironment` of these samples at these settings. */
  environment: number | null;
  office: Office;
  samples: Record<string, StateSample>;
  settings: ModelSettings;
}): string | null => {
  const rows = buildRows({
    county2024,
    edge,
    environment,
    external: null,
    layer: "foresight",
    office,
    races: RACES,
    samples,
    settings,
  });
  if (!rows.some((row) => row.estimate)) return null;
  const lines =
    office === "house"
      ? houseLines(
          rows,
          buildDistrictRows({
            county2024,
            edge,
            external: null,
            layer: "foresight",
            samples,
            settings,
          })
        )
      : office === "governor"
        ? governorLines(rows)
        : senateLines(rows, settings);

  return [
    ...lines,
    "",
    `How Foresight reads a race: ${blendText(settings)} (neighbors: how respondents expect the people around them to vote)${adjustmentsText(rows, environment)}.`,
    weightingNote(edge),
  ].join("\n");
};

/**
 * A race's figures as plain text, for its AI summary to be written from.
 * Built from the numbers its panel shows, so the two can't disagree. Null
 * when the office has no Foresight read.
 */
export const raceFigures = ({
  county2024,
  edge,
  environment,
  office,
  row,
  settings,
}: {
  county2024: County2024 | null;
  /** The `turnoutEdge` the row was built with. */
  edge: number;
  /** The `nationalEnvironment` the row was built with. */
  environment: number | null;
  office: Office;
  row: RaceRow;
  settings: ModelSettings;
}): string | null => {
  const { estimate, race, sample, state } = row;
  if (!estimate || !sample) return null;
  const context = { county2024, edge, environment, settings, state };
  const left = sideName(race, "left");
  const right = sideName(race, "right");
  const leader = estimate.margin >= 0 ? left : right;
  const leftChance = Math.round(estimate.leftProbability * 100);
  const leftVote = Math.round(estimate.leftShare * 100);
  const strength = strengthFor(estimate.leftProbability);
  const shareMargin = (leftShare: number) =>
    marginText((2 * leftShare - 1) * 100, left, right);
  const read = (component: Component | null) =>
    component ? shareMargin(component.leftShare) : "no answers";
  const blend =
    estimate.own && estimate.neighbors
      ? blendText(settings)
      : estimate.own
        ? "own vote only"
        : "neighbors only";
  const raceLines = race
    ? [
        `Candidates: ${describeSide(race, "left")} vs ${describeSide(race, "right")}${race.others.length > 0 ? `; also ${race.others.map((c) => `${c.name} (${c.party})`).join(", ")}` : ""}`,
        `Seat held by: ${race.incumbentParty} (incumbent ${race.incumbent}, ${race.incumbentRunning ? "running" : "not running"})`,
      ]
    : ["Candidates: generic ballot, Democratic vs Republican House candidate"];

  return [
    `Race: ${state.name} · ${OFFICE_LABEL[office]}${race?.special ? " (special)" : ""}`,
    ...raceLines,
    "",
    "Foresight forecast:",
    `- Margin: ${marginText(estimate.margin, left, right)}`,
    `- Two-party vote: ${left} ${leftVote}% · ${right} ${100 - leftVote}%`,
    `- Win probability: ${leader} ${estimate.margin >= 0 ? leftChance : 100 - leftChance}%`,
    `- Rating: ${strength === "tossup" ? STRENGTH_LABEL.tossup : `${STRENGTH_LABEL[strength]} ${leader}`}`,
    `- Uncertainty (95%): ±${(1.96 * estimate.sigma).toFixed(0)} points on the margin`,
    "",
    "How Foresight reads it:",
    `- Own vote, calibrated: ${read(estimate.own)}`,
    `- Neighbors (how respondents expect the people around them to vote): ${read(estimate.neighbors)}`,
    ...(estimate.own && estimate.independentDiscount !== null
      ? [
          `- Independent discount (the independent's simulated support runs far beyond the state's partisanship, and independents usually fade by Election Day): the own vote keeps ${formatPercent(INDEPENDENT_KEEP)} of its gap to ${state.name}'s 2024 presidential result (${marginText((1 - 2 * trumpTwoParty(state)) * 100, "Harris", "Trump")}), so it counts as ${marginText((2 * estimate.own.leftShare - 1) * 100 + estimate.independentDiscount, left, right)}`,
        ]
      : []),
    `- Blend: ${blend}`,
    ...(estimate.incumbencyBonus !== null
      ? [
          `- Incumbency bonus (governor running for re-election): ${marginText(estimate.incumbencyBonus, left, right)} added to the margin`,
        ]
      : []),
    ...(estimate.governorPull !== null && environment !== null
      ? [
          `- National pull (the margin keeps ${formatPercent(GOVERNOR_PULL)} of its distance from the national environment, the generic ballot at ${marginText(environment, "Democrats", "Republicans")}): ${marginText(estimate.governorPull, left, right)} added to the margin`,
        ]
      : []),
    ...(estimate.polls
      ? ["- Public polling is used as context in this read."]
      : []),
    "",
    `Undecided: ${formatPercent(estimate.undecided)} of likely voters`,
    "",
    "By group (own vote, likely-voter weighted):",
    ...crosstabs(sample, office, context).flatMap((tab) =>
      tab.leftShare === null
        ? []
        : [
            `- ${tab.group} · ${tab.label}: ${shareMargin(tab.leftShare)} (n=${tab.n})`,
          ]
    ),
    "",
    "By 2024 vote (own vote, likely-voter weighted):",
    ...recallCrosstabs(sample, office, context).flatMap((tab) =>
      tab.n === 0
        ? []
        : [
            `- ${tab.label} (n=${tab.n}): ${left} ${formatPercent(tab.left)} · ${right} ${formatPercent(tab.right)} · other ${formatPercent(tab.other)} · undecided ${formatPercent(tab.undecided)}`,
          ]
    ),
    "",
    weightingNote(edge),
  ].join("\n");
};

/** Key of a race's published summary: "GA-senate", "FL-senate-special". */
export const raceKey = (office: Office, row: RaceRow): string =>
  `${row.state.abbr}-${office}${row.race?.special ? "-special" : ""}`;

/** "; governors running for re-election get…" for each race adjustment
    applied on the map. */
const adjustmentsText = (
  rows: RaceRow[],
  environment: number | null
): string => {
  const appliedIn = (
    key: "governorPull" | "incumbencyBonus" | "independentDiscount" | "polls"
  ) =>
    rows.flatMap((row) =>
      row.estimate !== null && row.estimate[key] !== null
        ? [row.state.name]
        : []
    );
  const discounted = appliedIn("independentDiscount");
  return [
    ...(appliedIn("incumbencyBonus").length > 0
      ? [
          `governors running for re-election get ${GOVERNOR_INCUMBENCY} points on the margin`,
        ]
      : []),
    ...(discounted.length > 0
      ? [
          `in ${discounted.join(", ")}, where the independent's simulated support runs far beyond the state's partisanship, their own vote keeps ${formatPercent(INDEPENDENT_KEEP)} of its gap to the state's 2024 presidential result`,
        ]
      : []),
    ...(environment !== null && appliedIn("governorPull").length > 0
      ? [
          `governor margins then keep ${formatPercent(GOVERNOR_PULL)} of their distance from the national environment (the generic ballot, ${marginText(environment, "Democrats", "Republicans")})`,
        ]
      : []),
    ...(appliedIn("polls").length > 0
      ? ["where a race has been polled, public polling is used as context"]
      : []),
  ]
    .map((clause) => `; ${clause}`)
    .join("");
};

const blendText = (settings: ModelSettings): string =>
  `${Math.round(settings.ownWeight * 100)}% own vote / ${Math.round((1 - settings.ownWeight) * 100)}% neighbors`;

/** "PA-07: Smith (D) +0.4 points, 51% to win; seat held by R". */
const districtLine = (row: DistrictRow): string => {
  const { decided, district, projection } = row;
  const [leftParty, rightParty] = districtSides(district);
  const lead = decided
    ? `decided for ${PARTY_NAME[decided]} (finalists of one party, or a single candidate)`
    : projection
      ? `${marginText(projection.margin, nominee(district.left, leftParty), nominee(district.right, rightParty))}, ${formatPercent(Math.max(projection.leftProbability, 1 - projection.leftProbability))} to win`
      : "not read";
  return `${district.id}: ${lead}; seat held by ${district.holder}`;
};

const governorLines = (rows: RaceRow[]): string[] => {
  const counts = governorCounts(rows);
  const unread = rows.filter((row) => !row.estimate).length;
  const notUp =
    GOVERNOR_HOLDOVERS.D + GOVERNOR_HOLDOVERS.I + GOVERNOR_HOLDOVERS.R;

  return [
    `Office: Governor · ${rows.length} races on the November 3, 2026 ballot; ${notUp} governors not up (${GOVERNOR_HOLDOVERS.D} D, ${GOVERNOR_HOLDOVERS.R} R)`,
    `Foresight forecast · ${surveyedText(rows, "races")}`,
    `- Governorships after the election, governors not up included: Democrats ${counts.D} · Republicans ${counts.R}${counts.I > 0 ? ` · independents ${counts.I}` : ""} · toss-ups ${counts.open - unread}${unread > 0 ? ` · not surveyed ${unread}` : ""}`,
    "- Toss-up: neither side has a 60% chance to win",
    "",
    ...raceListLines(rows),
  ];
};

const houseLines = (rows: RaceRow[], districts: DistrictRow[]): string[] => {
  const generic = nationalGeneric(
    rows.flatMap((row) =>
      row.estimate ? [{ estimate: row.estimate, state: row.state }] : []
    ),
    STATES.filter((s) => s.abbr !== "DC")
  );
  const seats = houseProjection(districts);
  const contested = districts.flatMap((row) =>
    row.projection ? [{ projection: row.projection, row }] : []
  );
  const closest = [...contested]
    .sort(
      (a, b) =>
        Math.abs(a.projection.leftProbability - 0.5) -
        Math.abs(b.projection.leftProbability - 0.5)
    )
    .slice(0, CLOSEST);
  const flips = districts.filter((row) => row.flip);
  const gains = (party: Party) =>
    flips.filter((row) => row.call.leader === party).length;
  const examples = [...flips]
    .sort((a, b) => leaderChance(b) - leaderChance(a))
    .slice(0, FLIP_EXAMPLES);
  const fromNational = contested.filter(
    ({ projection }) => projection.basis === "national"
  ).length;
  const decided = districts.filter((row) => row.decided).length;

  return [
    `Office: U.S. House · all ${districts.length} seats on the November 3, 2026 ballot; 218 for control`,
    `Foresight forecast · ${surveyedText(rows, "states")}`,
    ...(generic
      ? [
          `- National House vote (generic ballot, each surveyed state weighted by its 2024 turnout): ${marginText(generic.margin, "Democrats", "Republicans")}; the surveyed states cast ${formatPercent(generic.coverage)} of the 2024 vote`,
        ]
      : []),
    ...(seats
      ? [
          `- Expected seats: ${seatsText(seats.expected, 0)}`,
          `- Control: Democrats ${formatPercent(seats.probabilities.demControl)} · Republicans ${formatPercent(seats.probabilities.repControl)}${seats.probabilities.independentsDecide >= 0.005 ? ` · no majority ${formatPercent(seats.probabilities.independentsDecide)}` : ""}`,
        ]
      : []),
    `- Seats decided whatever happens (finalists of one party, or a single candidate): ${decided}`,
    ...(fromNational > 0
      ? [
          `- ${fromNational} contested seats are in states not surveyed yet: they take the surveyed states' average swing, with wider error`,
        ]
      : []),
    "- Each district starts from its 2024 presidential result and moves by its state's swing, plus its own counties' deviation once they have enough respondents",
    "",
    "Closest districts (leader, margin, leader's chance to win):",
    ...closest.map(({ row }) => `- ${districtLine(row)}`),
    "",
    `Projected seat flips (leader differs from the party holding the seat): ${flips.length} (Democrats gain ${gains("D")}, Republicans gain ${gains("R")}${gains("I") > 0 ? `, independents gain ${gains("I")}` : ""})`,
    ...(examples.length > 0 ? ["Most likely flips:"] : []),
    ...examples.map((row) => `- ${districtLine(row)}`),
  ];
};

/** The projected leader's chance to win a district (1 when decided). */
const leaderChance = (row: DistrictRow): number => {
  const p = row.call.leftProbability;
  return p === null ? 0 : Math.max(p, 1 - p);
};

const marginText = (margin: number, left: string, right: string): string =>
  Math.abs(margin) < 0.05
    ? "even"
    : `${margin > 0 ? left : right} +${Math.abs(margin).toFixed(1)} points`;

const nominee = (candidate: Candidate | null, party: Party): string =>
  candidate ? `${shortName(candidate)} (${party})` : PARTY_NAME[party];

/** "Georgia: Ossoff (D) +1.2 points, 56% to win; seat held by D". */
const raceLine = (row: RaceRow, estimate: ForesightEstimate): string => {
  const left = `${sideName(row.race, "left")} (${row.leftParty})`;
  const right = `${sideName(row.race, "right")} (R)`;
  const chance = Math.max(
    estimate.leftProbability,
    1 - estimate.leftProbability
  );
  return `${row.state.name}${row.race?.special ? " (special)" : ""}: ${marginText(estimate.margin, left, right)}, ${formatPercent(chance)} to win${row.race ? `; seat held by ${row.race.incumbentParty}` : ""}`;
};

/** Closest races and projected flips of a Senate or governor map. */
const raceListLines = (rows: RaceRow[]): string[] => {
  const read = rows.flatMap((row) =>
    row.estimate ? [{ estimate: row.estimate, row }] : []
  );
  const closest = [...read]
    .sort(
      (a, b) =>
        Math.abs(a.estimate.leftProbability - 0.5) -
        Math.abs(b.estimate.leftProbability - 0.5)
    )
    .slice(0, CLOSEST);
  const flips = read.filter(({ row }) => row.flip);

  return [
    "Closest races (leader, margin, leader's chance to win):",
    ...closest.map(({ estimate, row }) => `- ${raceLine(row, estimate)}`),
    "",
    "Projected flips (leader differs from the party holding the seat):",
    ...(flips.length > 0
      ? flips.map(({ estimate, row }) => `- ${raceLine(row, estimate)}`)
      : ["- None"]),
  ];
};

const seatsText = (expected: Record<Party, number>, digits: number): string =>
  [
    `Democrats ${expected.D.toFixed(digits)}`,
    `Republicans ${expected.R.toFixed(digits)}`,
    ...(expected.I >= 0.5 * 10 ** -digits
      ? [`independents ${expected.I.toFixed(digits)}`]
      : []),
  ].join(" · ");

const senateLines = (rows: RaceRow[], settings: ModelSettings): string[] => {
  const { expected, priors, probabilities } = senateProjection(
    rows,
    "foresight",
    settings
  );
  const notUp = SENATE_HOLDOVERS.D + SENATE_HOLDOVERS.I + SENATE_HOLDOVERS.R;

  return [
    `Office: U.S. Senate · ${rows.length} seats on the November 3, 2026 ballot; ${notUp} senators not up`,
    `Foresight forecast · ${surveyedText(rows, "races")}`,
    `- Expected seats after the election, senators not up included (independents who caucus with Democrats counted as Democrats): ${seatsText(expected, 1)}`,
    `- Control: Democrats ${formatPercent(probabilities.demControl)} · Republicans ${formatPercent(probabilities.repControl)} · independents decide (neither party has control alone) ${formatPercent(probabilities.independentsDecide)}`,
    "- Democrats need 51 seats; the Vice President breaks 50-50 ties for Republicans",
    ...(priors > 0
      ? [
          `- ${priors} race${priors === 1 ? "" : "s"} not surveyed yet read from a placeholder rating`,
        ]
      : []),
    "",
    ...raceListLines(rows),
  ];
};

const sideName = (race: Race | null, side: Side): string => {
  const candidate = race?.[side];
  if (candidate) return shortName(candidate);
  return side === "left" ? "Democrats" : "Republicans";
};

const surveyedText = (rows: RaceRow[], unit: string): string =>
  `${rows.filter((row) => row.estimate).length} of ${rows.length} ${unit} surveyed`;

const weightingNote = (edge: number): string =>
  `Shares are of likely voters: weighted by stated turnout, calibrated to the certified 2024 result, then to midterm turnout (2024 non-voters capped at ${formatPercent(NON_VOTER_SHARE)} of likely voters, Harris voters weighted ×${edge.toFixed(2)} and Trump voters ×${(1 / edge).toFixed(2)} nationally).`;
